import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BeneficiarioRepository, BeneficiarioFiltroEstructurado } from 'src/app/domain/service/beneficiario/beneficiario-repository';
import { Beneficiario, BeneficiarioPageResponse, CentroTrabajoPageResponse } from 'src/app/domain/model/beneficiario/beneficiario';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP: HttpBeneficiarioRepository
 *
 * Implementa el puerto BeneficiarioRepository para comunicación con backend.
 * Realiza llamadas REST al BeneficiarioController en servicios-ms.
 *
 * Migrado del legacy GWT BuscadorBeneficiariosActivity.
 *
 * Arquitectura Hexagonal:
 * - Este adaptador está en infraestructura
 * - Implementa el puerto BeneficiarioRepository del dominio
 *
 * @author Ingeneo - Migración GWT
 */
@Injectable({ providedIn: 'root' })
export class HttpBeneficiarioRepository implements BeneficiarioRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/beneficiarios`;

  /**
   * Busca beneficiarios por filtro de texto
   */
  buscarPorFiltro(filtro: string, limit: number = 50): Promise<Beneficiario[]> {
    const params = new HttpParams()
      .set('filtro', filtro)
      .set('limit', limit.toString());

    return firstValueFrom(
      this.http.get<Beneficiario[]>(this.baseUrl, { params })
    );
  }

  /**
   * Busca beneficiarios con filtros estructurados (legacy: getWhereClauses).
   * Cada campo se envía como query param individual al backend.
   * El backend genera WHERE independientes con AND.
   * Paginación keyset/cursor (ver skill acceso-datos-oracle §7).
   *
   * Legacy: Tpsc_beneficiarioPersistenceServicesImpl.consultar()
   */
  buscarConFiltrosEstructurados(
    filtros: BeneficiarioFiltroEstructurado,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<BeneficiarioPageResponse> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', size.toString())
      .set('direction', direction);

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    if (filtros.tipoIdentificacion) {
      params = params.set('tipoId', filtros.tipoIdentificacion);
    }
    if (filtros.numeroIdentificacion) {
      params = params.set('numId', filtros.numeroIdentificacion);
    }
    if (filtros.contrato) {
      params = params.set('contrato', filtros.contrato);
    }
    if (filtros.nombreComercial) {
      params = params.set('nombreComercial', filtros.nombreComercial);
    }
    if (filtros.nombre1) {
      params = params.set('nombre1', filtros.nombre1);
    }
    if (filtros.nombre2) {
      params = params.set('nombre2', filtros.nombre2);
    }
    if (filtros.apellido1) {
      params = params.set('apellido1', filtros.apellido1);
    }
    if (filtros.apellido2) {
      params = params.set('apellido2', filtros.apellido2);
    }

    return firstValueFrom(
      this.http.get<BeneficiarioPageResponse>(`${this.baseUrl}/search`, { params })
    );
  }

  /**
   * Obtiene un beneficiario por su consecutivo
   */
  obtenerPorConsecutivo(nmconsecutivo: number): Promise<Beneficiario | null> {
    return firstValueFrom(
      this.http.get<Beneficiario>(`${this.baseUrl}/${nmconsecutivo}`)
    ).catch(() => null);
  }

  /**
   * Busca centros de trabajo con paginación keyset/cursor unificada (1 sola
   * llamada HTTP). Retorna content + totalElements en una respuesta.
   * Usa la misma URL registrada en seguridad: GET /api/servicios/beneficiarios/{id}/centros-trabajo
   */
  buscarCentrosTrabajoPaginado(
    nmconsecutivoBenef: number,
    filtro: string | undefined,
    codigo: string | undefined,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<CentroTrabajoPageResponse> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', size.toString())
      .set('direction', direction);

    if (filtro) {
      params = params.set('filtro', filtro);
    }
    if (codigo) {
      params = params.set('codigo', codigo);
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return firstValueFrom(
      this.http.get<CentroTrabajoPageResponse>(
        `${this.baseUrl}/${nmconsecutivoBenef}/centros-trabajo`,
        { params }
      )
    );
  }
}
